import React, { useState, useEffect } from 'react';
import { getAllVenues, createNewVenue, updateVenueKundenummer, removeVenueKundenummer } from '../../cash/utils/loomisVenueManager';
import { supabase } from '../../../lib/supabase';

const Settings = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateVenueModal, setShowCreateVenueModal] = useState(false);
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [newVenueForm, setNewVenueForm] = useState({ venueName: '', tripletexId: '', tripletexNumber: '', kundenummer: '', vippsId: '', foodoraName: '' });
  const [editVenueForm, setEditVenueForm] = useState({ venueName: '', departmentName: '', tripletexId: '', tripletexNumber: '', vippsId: '', foodoraName: '' });
  const [newKundenummer, setNewKundenummer] = useState('');

  useEffect(() => { loadVenues(); }, []);

  const loadVenues = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAllVenues();
      if (result.success) setVenues(result.venues);
      else setError(result.error);
    } catch (err) {
      setError(`Failed to load venues: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVenue = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    try {
      const result = await createNewVenue(newVenueForm);
      if (result.success) {
        setSuccess('Venue created.');
        setShowCreateVenueModal(false);
        setNewVenueForm({ venueName: '', tripletexId: '', tripletexNumber: '', kundenummer: '', vippsId: '', foodoraName: '' });
        await loadVenues();
      } else setError(result.error);
    } catch (err) { setError(`Failed to create venue: ${err.message}`); }
  };

  const handleEditVenue = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    try {
      const { error: updateError } = await supabase
        .from('venues')
        .update({
          wolt_venue_name: editVenueForm.venueName,
          department_name: editVenueForm.departmentName || null,
          venue_tripletexid: editVenueForm.tripletexId,
          venue_tripletex_number: editVenueForm.tripletexNumber,
          vipps_id: editVenueForm.vippsId || null,
          foodora_name: editVenueForm.foodoraName || null
        })
        .eq('id', selectedVenue.id);
      if (updateError) throw new Error(updateError.message);
      setSuccess('Venue updated.');
      setShowVenueModal(false);
      setSelectedVenue(null);
      await loadVenues();
    } catch (err) { setError(`Failed to update venue: ${err.message}`); }
  };

  const handleAddKundenummer = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    try {
      const result = await updateVenueKundenummer(selectedVenue.id, newKundenummer);
      if (result.success) {
        setSuccess('Kundenummer added.');
        setNewKundenummer('');
        await loadVenues();
        const updatedVenue = venues.find(v => v.id === selectedVenue.id);
        if (updatedVenue) setSelectedVenue(updatedVenue);
      } else setError(result.error);
    } catch (err) { setError(`Failed to add kundenummer: ${err.message}`); }
  };

  const handleRemoveKundenummer = async (venueId, kundenummer) => {
    if (!window.confirm(`Remove kundenummer "${kundenummer}"?`)) return;
    setError(null); setSuccess(null);
    try {
      const result = await removeVenueKundenummer(venueId, kundenummer);
      if (result.success) {
        setSuccess('Kundenummer removed.');
        await loadVenues();
        const updatedVenue = venues.find(v => v.id === selectedVenue.id);
        if (updatedVenue) setSelectedVenue(updatedVenue);
      } else setError(result.error);
    } catch (err) { setError(`Failed to remove kundenummer: ${err.message}`); }
  };

  const openVenueModal = (venue) => {
    setSelectedVenue(venue);
    setEditVenueForm({
      venueName: venue.wolt_venue_name,
      departmentName: venue.department_name || '',
      tripletexId: venue.venue_tripletexid,
      tripletexNumber: venue.venue_tripletex_number || '',
      vippsId: venue.vipps_id || '',
      foodoraName: venue.foodora_name || ''
    });
    setNewKundenummer('');
    setShowVenueModal(true);
  };

  const closeModals = () => {
    setShowCreateVenueModal(false);
    setShowVenueModal(false);
    setSelectedVenue(null);
    setNewVenueForm({ venueName: '', tripletexId: '', tripletexNumber: '', kundenummer: '', vippsId: '', foodoraName: '' });
    setEditVenueForm({ venueName: '', departmentName: '', tripletexId: '', tripletexNumber: '', vippsId: '', foodoraName: '' });
    setNewKundenummer('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Loading venues...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <header className="mb-8">
          <h1 className="text-xl font-semibold text-black">Settings</h1>
          <p className="mt-1 text-sm text-gray-400">Manage venues and their identifiers.</p>
        </header>

        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
        {success && <p className="text-sm text-gray-600 mb-4">{success}</p>}

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-black">Venues ({venues.length})</h2>
          <button onClick={() => setShowCreateVenueModal(true)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
            Add venue
          </button>
        </div>

        <p className="text-xs text-gray-400 mb-4">Click a row to edit venue details and manage Loomis kundenummers.</p>

        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Department</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Wolt name</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Tripletex ID</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Tripletex #</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Vipps ID</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Foodora</th>
                  <th className="text-left py-2.5 px-4 font-medium text-gray-500">Loomis</th>
                </tr>
              </thead>
              <tbody>
                {venues.map((venue) => (
                  <tr
                    key={venue.id}
                    onClick={() => openVenueModal(venue)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="py-2.5 px-4 font-medium text-black">{venue.department_name || '–'}</td>
                    <td className="py-2.5 px-4 text-gray-500">{venue.wolt_venue_name}</td>
                    <td className="py-2.5 px-4 text-gray-500">{venue.venue_tripletexid}</td>
                    <td className="py-2.5 px-4 text-gray-500">{venue.venue_tripletex_number || '–'}</td>
                    <td className="py-2.5 px-4 text-gray-500">{venue.vipps_id || '–'}</td>
                    <td className="py-2.5 px-4 text-gray-500">{venue.foodora_name || '–'}</td>
                    <td className="py-2.5 px-4">
                      {venue.kundenummers && venue.kundenummers.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {venue.kundenummers.map((k, i) => (
                            <span key={i} className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">{k}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">–</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {venues.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-gray-400">
              No venues found. Create one to get started.
            </div>
          )}
        </div>

        {/* Create venue modal */}
        {showCreateVenueModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={closeModals}>
            <div className="w-full max-w-lg bg-white border border-gray-200 rounded-md shadow-lg" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-black">Create new venue</h2>
                <button onClick={closeModals} className="text-gray-400 hover:text-black transition-colors">&times;</button>
              </div>
              <form onSubmit={handleCreateVenue}>
                <div className="px-5 py-4 space-y-3">
                  {[
                    { id: 'venueName', label: 'Venue name *', field: 'venueName', required: true },
                    { id: 'tripletexId', label: 'Tripletex ID *', field: 'tripletexId', required: true },
                    { id: 'tripletexNumber', label: 'Tripletex number', field: 'tripletexNumber' },
                    { id: 'vippsId', label: 'Vipps ID', field: 'vippsId' },
                    { id: 'foodoraName', label: 'Foodora name', field: 'foodoraName' },
                    { id: 'kundenummer', label: 'Kundenummer', field: 'kundenummer' },
                  ].map(({ id, label, field, required }) => (
                    <div key={id}>
                      <label htmlFor={id} className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                      <input
                        type="text" id={id} value={newVenueForm[field]}
                        onChange={(e) => setNewVenueForm({ ...newVenueForm, [field]: e.target.value })}
                        required={required}
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
                  <button type="button" onClick={closeModals} className="px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                  <button type="submit" className="px-3 py-2 text-sm bg-black text-white rounded-md hover:bg-gray-900 transition-colors">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit venue modal */}
        {showVenueModal && selectedVenue && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={closeModals}>
            <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white border border-gray-200 rounded-md shadow-lg flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between shrink-0">
                <h2 className="text-sm font-semibold text-black">
                  {selectedVenue.department_name || selectedVenue.wolt_venue_name}
                </h2>
                <button onClick={closeModals} className="text-gray-400 hover:text-black transition-colors">&times;</button>
              </div>

              <div className="flex-1 overflow-auto px-5 py-4 space-y-6">
                {/* Venue info */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Venue information</h3>
                  <form onSubmit={handleEditVenue}>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      {[
                        { id: 'editDepartmentName', label: 'Department name', field: 'departmentName' },
                        { id: 'editVenueName', label: 'Wolt venue name *', field: 'venueName', required: true },
                        { id: 'editTripletexId', label: 'Tripletex ID *', field: 'tripletexId', required: true },
                        { id: 'editTripletexNumber', label: 'Tripletex number', field: 'tripletexNumber' },
                        { id: 'editVippsId', label: 'Vipps ID', field: 'vippsId' },
                        { id: 'editFoodoraName', label: 'Foodora name', field: 'foodoraName' },
                      ].map(({ id, label, field, required }) => (
                        <div key={id}>
                          <label htmlFor={id} className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                          <input
                            type="text" id={id} value={editVenueForm[field]}
                            onChange={(e) => setEditVenueForm({ ...editVenueForm, [field]: e.target.value })}
                            required={required}
                            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors"
                          />
                        </div>
                      ))}
                    </div>
                    <button type="submit" className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors">
                      Update venue
                    </button>
                  </form>
                </div>

                {/* Kundenummer management */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Loomis kundenummers</h3>

                  {selectedVenue.kundenummers && selectedVenue.kundenummers.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedVenue.kundenummers.map((k, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                          {k}
                          <button
                            onClick={() => handleRemoveKundenummer(selectedVenue.id, k)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Remove"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mb-4">No kundenummers assigned.</p>
                  )}

                  <form onSubmit={handleAddKundenummer} className="flex gap-2">
                    <input
                      type="text"
                      value={newKundenummer}
                      onChange={(e) => setNewKundenummer(e.target.value)}
                      required
                      placeholder="Enter kundenummer"
                      className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors"
                    />
                    <button type="submit" className="px-3 py-2 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors shrink-0">
                      Add
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
